/**
 * フォーム回答・返信の型と入力検証（02 5.3.16 form-submission・要件書 2.3・付録 No.15）。
 * - ステータスは旧PCRMと同じ 新規 new・未対応 unhandled・対応中 in_progress・完了 completed・スパム spam（265）。API-FSUB-04 でどれからどれへも変えられる
 * - is_review_required は false にだけ変えられる（true は 400）
 * - 一覧（API-FSUB-01）は answer_map・attachments・replies を含めない。詳細（API-FSUB-02）は answer_items を組み立てて返す
 */
import { z } from 'zod';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { FORM_SUBMISSION_STATUS_VALUES } from '../enums/form-submission-status';
import { PRIORITY_TYPE_VALUES } from '../enums/priority-type';
import { zCode, zDateTime, zEnum, zNullable, zRequiredText, zShortText, zSystemCode, zVersion } from './common.schema';
import { AttachmentSchema } from './attachment.schema';
import { FORM_FIELD_TYPE_VALUES } from './form.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const FormReplySchema = z.object({
  form_submission_code: z.string(),
  sent_at: z.string(),
  subject: z.string(),
  reply_body: z.string(),
  sent_by: z.string(),
  sent_by_name: z.string().nullable(),
});

export const FormSubmissionSchema = z
  .object({
    form_submission_code: z.string(),
    form_code: z.string(),
    form_name: z.string().nullable(),
    version_number: z.number().int(),
    submitted_at: z.string(),
    form_submission_status: z.enum(FORM_SUBMISSION_STATUS_VALUES),
    priority_type: z.enum(PRIORITY_TYPE_VALUES),
    assignee_user_code: z.string().nullable(),
    assignee_user_name: z.string().nullable(),
    customer_code: z.string().nullable(),
    customer_name: z.string().nullable(),
    customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
    customer_deleted: z.boolean(),
    submitter_name: z.string().nullable(),
    submitter_email: z.string().nullable(),
    submitter_phone_number: z.string().nullable(),
    submitter_company_name: z.string().nullable(),
    started_at: z.string().nullable(),
    completed_at: z.string().nullable(),
    internal_memo: z.string().nullable(),
    is_review_required: z.boolean(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const FormAnswerItemSchema = z.object({
  field_key: z.string(),
  label: z.string(),
  field_type: z.enum(FORM_FIELD_TYPE_VALUES).nullable(),
  value: z.unknown(),
});

export const FormSubmissionDetailSchema = FormSubmissionSchema.extend({
  answer_map: z.record(z.unknown()),
  answer_items: z.array(FormAnswerItemSchema),
  ip_address: z.string().nullable(),
  /** 265：公開フォーム送信時の User-Agent（旧 submitter_info.user_agent） */
  user_agent: z.string().nullable(),
  referrer_url: z.string().nullable(),
  utm_source: z.string().nullable(),
  utm_medium: z.string().nullable(),
  utm_campaign: z.string().nullable(),
  /** 265：スパムスコア（旧と同じく受付時に 0。判定はしない）。値が無い回答は null */
  spam_score: z.number().nullable(),
  /** 265：回答キー → 見出し（旧の responses[key].label を移行したもの）。answer_items の見出しは 定義 → これ → キー の順 */
  answer_label_map: z.record(z.string()).nullable(),
  attachments: z.array(AttachmentSchema),
  replies: z.array(FormReplySchema),
});

/** API-FSUB-04 回答の更新（優先度・担当者・内部メモ・ステータス・要確認を外す） */
export const FormSubmissionUpdateSchema = z
  .object({
    form_submission_status: zEnum(FORM_SUBMISSION_STATUS_VALUES).optional(),
    priority_type: zEnum(PRIORITY_TYPE_VALUES).optional(),
    assignee_user_code: zNullable(zSystemCode).optional(),
    internal_memo: zShortText(5000).optional(),
    is_review_required: z.literal(false, { message: 'MSG-V-020' }).optional(),
    version: zVersion,
  })
  .strict();

/** API-FSUB-05 顧客への紐付け（null で外す） */
export const FormSubmissionCustomerSchema = z.object({ customer_code: zNullable(zCode), version: zVersion }).strict();

/** API-FSUB-06 回答者へ返信 */
export const FormReplyCreateSchema = z
  .object({
    subject: zRequiredText(500, 'MSG-V-056'),
    reply_body: zRequiredText(10000, 'MSG-V-091'),
    version: zVersion,
  })
  .strict();

export const FormReplyResultSchema = z.object({ reply: FormReplySchema, form_submission: FormSubmissionSchema });

export const FORM_SUBMISSION_LIST_SORT_VALUES = ['submitted_at', 'priority_type', 'form_submission_status'] as const;

const zBooleanQuery = z.preprocess(
  (value) => (value === 'true' ? true : value === 'false' ? false : value),
  z.boolean({ message: 'MSG-V-020' }),
);

/** API-FSUB-01（02 2.3.2）。送信日時の範囲は UTC の ISO 文字列（両端を含む） */
export const FormSubmissionListQuerySchema = buildListQuerySchema(
  FORM_SUBMISSION_LIST_SORT_VALUES,
  '-submitted_at',
).extend({
  form_code: zCode.optional(),
  form_submission_status: zEnum(FORM_SUBMISSION_STATUS_VALUES).optional(),
  priority_type: zEnum(PRIORITY_TYPE_VALUES).optional(),
  assignee_user_code: zSystemCode.optional(),
  is_review_required: zBooleanQuery.optional(),
  submitted_at_from: zDateTime.optional(),
  submitted_at_to: zDateTime.optional(),
});

export type FormReply = z.infer<typeof FormReplySchema>;
export type FormSubmission = z.infer<typeof FormSubmissionSchema>;
export type FormSubmissionDetail = z.infer<typeof FormSubmissionDetailSchema>;
export type FormAnswerItem = z.infer<typeof FormAnswerItemSchema>;
export type FormSubmissionUpdateInput = z.infer<typeof FormSubmissionUpdateSchema>;
export type FormSubmissionCustomerInput = z.infer<typeof FormSubmissionCustomerSchema>;
export type FormReplyCreateInput = z.infer<typeof FormReplyCreateSchema>;
export type FormReplyResult = z.infer<typeof FormReplyResultSchema>;
export type FormSubmissionListQuery = z.infer<typeof FormSubmissionListQuerySchema>;
